import { apiClient } from './client.js';
import type {
  RegistrationResponse,
  Account,
  Tenant,
  TenantMembership,
  UpdateAccountProfileInput,
  UserRole,
} from '@atomfit/shared';

export interface AccountAuthResponse {
  accessToken: string;
  refreshToken: string;
  account: Account;
  requiresPasswordSetup: boolean;
  tenant?: Pick<Tenant, 'name' | 'slug' | 'type'>;
  role: UserRole | null;
  availableTenants: TenantMembership[];
}

export const authApi = {
  lookup: (email: string) =>
    apiClient.post<{ method: 'password' | 'magic-link' | 'register'; phoneRequired: boolean }>(
      '/auth/lookup',
      { email },
    ),

  login: (email: string, password: string) =>
    apiClient.post<AccountAuthResponse>('/auth/login', { email, password }),

  register: (
    email: string,
    password: string,
    firstName: string | undefined,
    lastName: string | undefined,
    phone: string,
    referralCode?: string,
    redirectTo?: string,
  ) =>
    apiClient.post<RegistrationResponse>('/auth/register', {
      email,
      password,
      firstName,
      lastName,
      phone,
      referralCode,
      redirectTo,
    }),

  confirmEmailVerification: (token: string) =>
    apiClient.post<{ email: string }>('/auth/email-verification/confirm', { token }),

  resendEmailVerification: (email: string, redirectTo?: string) =>
    apiClient.post('/auth/email-verification/resend', { email, redirectTo }),

  switchTenant: (tenantSlug: string) =>
    apiClient.post<AccountAuthResponse>('/auth/switch-tenant', { tenantSlug }),

  logout: () => apiClient.post('/auth/logout'),

  requestMagicLink: (email: string, redirectTo?: string, phone?: string) =>
    apiClient.post('/auth/magic-link/request', { email, redirectTo, phone }),

  verifyMagicLink: (token: string) =>
    apiClient.post<AccountAuthResponse>('/auth/magic-link/verify', { token }),

  requestPasswordReset: (email: string) =>
    apiClient.post('/auth/password-reset/request', { email }),

  confirmPasswordReset: (token: string, password: string) =>
    apiClient.post('/auth/password-reset/confirm', { token, password }),

  setInitialPassword: (password: string) => apiClient.post('/auth/password', { password }),

  me: () => apiClient.get<{ account: Account; role: UserRole | null }>('/auth/me'),

  updateMe: (data: UpdateAccountProfileInput) =>
    apiClient.patch<{ account: Account; role: UserRole | null }>('/auth/me', data),
};
